import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';
import FilterPanel from '../components/FilterPanel';
import ProductGrid from '../components/ProductGrid';
import { ENDPOINTS, brandPath, categoryPath, withTrailingSlash } from '../config';

const ORDERBY_TO_SORT = {
    'price-desc': 'price_desc',
    price: 'price_asc',
    date: 'newest',
    popularity: 'newest',
    rating: 'newest',
    menu_order: 'newest',
};

const SORT_TO_ORDERBY = {
    price_desc: 'price-desc',
    price_asc: 'price',
    newest: 'date',
};

export default function Products() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Path params from the legacy WP URL forms:
    //   /product-category/{cat}(/{sub}(/{subsub})?)?(/page/{n})?
    //   /all_products/page/{n}, /product-tag/{tag}, /ram|storage|camera/{value}
    // Brand nav uses /product-category/all-products/{brand}/ (live form).
    const {
        categorySlug,
        subcategorySlug,
        subsubcategorySlug,
        pageNum,
        tagSlug,
        ramValue,
        storageValue,
        cameraValue,
    } = useParams();
    // WP had pseudo-categories that just meant "everything".
    const NON_FILTER_CATEGORIES = ['all-products', 'offers'];
    const isAllProductsBrand =
        categorySlug === 'all-products' &&
        Boolean(subcategorySlug) &&
        !subsubcategorySlug &&
        !NON_FILTER_CATEGORIES.includes(subcategorySlug);
    const pathBrand = isAllProductsBrand ? subcategorySlug : null;
    const effectiveCategory = subsubcategorySlug || subcategorySlug || categorySlug;
    const pathCategory =
        !pathBrand &&
        effectiveCategory &&
        !NON_FILTER_CATEGORIES.includes(effectiveCategory)
            ? effectiveCategory
            : null;
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

    // Read ALL filters from URL query params (supports dynamic attribute params);
    // path params act as defaults that explicit query params can override.
    const filters = Object.fromEntries(searchParams.entries());
    if (pathBrand && !filters.brand) filters.brand = pathBrand;
    if (pathCategory && !filters.category) filters.category = pathCategory;
    if (tagSlug && !filters.search) filters.search = tagSlug.replace(/-/g, ' ');
    if (ramValue && !filters.ram) filters.ram = ramValue;
    if (storageValue && !filters.storage) filters.storage = storageValue;
    if (cameraValue && !filters.camera) filters.camera = cameraValue;
    if (pageNum && !filters.page) filters.page = pageNum;
    // Live WP used ?orderby=; map to internal sort when sort is absent.
    if (!filters.sort && filters.orderby && ORDERBY_TO_SORT[filters.orderby]) {
        filters.sort = ORDERBY_TO_SORT[filters.orderby];
    }
    if (!filters.sort) filters.sort = 'newest';
    const currentPage = parseInt(filters.page) || 1;

    // Fetch products from API whenever query params change
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Pass all URL params directly to the API
            const params = new URLSearchParams(searchParams);
            if (pathBrand && !params.get('brand')) params.set('brand', pathBrand);
            if (pathCategory && !params.get('category')) params.set('category', pathCategory);
            if (tagSlug && !params.get('search')) params.set('search', tagSlug.replace(/-/g, ' '));
            if (ramValue && !params.get('ram')) params.set('ram', ramValue);
            if (storageValue && !params.get('storage')) params.set('storage', storageValue);
            if (cameraValue && !params.get('camera')) params.set('camera', cameraValue);
            if (pageNum && !params.get('page')) params.set('page', pageNum);
            if (!params.get('sort') && params.get('orderby') && ORDERBY_TO_SORT[params.get('orderby')]) {
                params.set('sort', ORDERBY_TO_SORT[params.get('orderby')]);
            }
            params.delete('orderby');
            params.set('limit', '20');

            const response = await fetch(`${ENDPOINTS.PRODUCTS.PUBLIC}?${params.toString()}`);
            const data = await response.json();

            if (data?.isOk && data?.data) {
                setProducts(data.data.products || []);
                setPagination(data.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
            } else {
                setProducts([]);
                setPagination({ page: 1, limit: 20, total: 0, totalPages: 0 });
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Unable to load products right now. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchParams.toString(), pathBrand, pathCategory, tagSlug, ramValue, storageValue, cameraValue, pageNum]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    /** Keep non-brand/category filters when switching listing paths. */
    const buildAuxParams = (overrides = {}) => {
        const params = new URLSearchParams(searchParams);
        Object.entries(overrides).forEach(([key, value]) => {
            if (key === 'brand' || key === 'category') return;
            if (value && value !== '' && value !== 0 && value !== '0') {
                params.set(key, String(value));
            } else {
                params.delete(key);
            }
        });
        params.delete('brand');
        params.delete('category');
        params.delete('page');
        params.delete('sort');
        return params;
    };

    /**
     * Single brand OR single category → live path URL (SEO parity).
     * Multiple brands/categories (or brand + category) → /products?… multi-filter.
     */
    const navigateListing = (brandStr, categoryStr, overrides = {}) => {
        const brands = String(brandStr || '').split(',').filter(Boolean);
        const cats = String(categoryStr || '').split(',').filter(Boolean);
        const aux = buildAuxParams(overrides);
        const qs = aux.toString();
        const navOpts = { replace: true, preventScrollReset: true };

        if (brands.length === 1 && cats.length === 0) {
            aux.set('orderby', aux.get('orderby') || 'price-desc');
            const q = aux.toString();
            navigate(q ? `${brandPath(brands[0])}?${q}` : brandPath(brands[0]), navOpts);
            return;
        }

        if (cats.length === 1 && brands.length === 0) {
            aux.delete('orderby');
            const q = aux.toString();
            navigate(q ? `${categoryPath(cats[0])}?${q}` : categoryPath(cats[0]), navOpts);
            return;
        }

        if (brands.length === 0 && cats.length === 0) {
            aux.delete('orderby');
            const q = aux.toString();
            const base = withTrailingSlash('/product-category/all-products');
            navigate(q ? `${base}?${q}` : base, navOpts);
            return;
        }

        // Multi-select combined listing
        aux.delete('orderby');
        if (brands.length) aux.set('brand', brands.join(','));
        if (cats.length) aux.set('category', cats.join(','));
        const q = aux.toString();
        const base = withTrailingSlash('/products');
        navigate(q ? `${base}?${q}` : base, navOpts);
    };

    const updateFilters = (newFilters) => {
        if ('brand' in newFilters || 'category' in newFilters) {
            const nextBrand =
                'brand' in newFilters ? String(newFilters.brand || '') : String(filters.brand || '');
            const nextCategory =
                'category' in newFilters
                    ? String(newFilters.category || '')
                    : String(filters.category || '');
            navigateListing(nextBrand, nextCategory, newFilters);
            return;
        }

        const params = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value && value !== '' && value !== 0 && value !== '0') {
                params.set(key, String(value));
            } else {
                params.delete(key);
            }
        });

        if (!('page' in newFilters)) {
            params.delete('page');
        }

        // Prefer live orderby on category/brand paths when sort changes
        if ('sort' in newFilters && newFilters.sort) {
            const orderby = SORT_TO_ORDERBY[newFilters.sort];
            if (orderby && location.pathname.includes('/product-category/')) {
                params.set('orderby', orderby);
                params.delete('sort');
            }
        }

        setSearchParams(params, { replace: true, preventScrollReset: true });
    };

    const handleSortChange = (newSort) => {
        updateFilters({ sort: newSort });
    };

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');

    const getPageTitle = () => {
        if (filters.brand && filters.category) {
            return `${capitalize(filters.brand.split(',')[0])} - ${capitalize(filters.category.split(',')[0])}`;
        }
        if (filters.brand) return `${capitalize(filters.brand.split(',')[0])} Products`;
        if (filters.category) return capitalize(filters.category.split(',')[0]);
        if (filters.search) return `Search: "${filters.search}"`;
        return 'All Products';
    };

    const getActiveFilterChips = () => {
        const chips = [];
        
        Object.entries(filters).forEach(([key, value]) => {
            if (!value || key === 'sort' || key === 'page' || key === 'showAll' || key === 'orderby') return;
            
            if (key === 'minPrice') {
                chips.push({ key, type: 'minPrice', label: `Min: KSh ${parseInt(value).toLocaleString()}`, value });
            } else if (key === 'maxPrice') {
                chips.push({ key, type: 'maxPrice', label: `Max: KSh ${parseInt(value).toLocaleString()}`, value });
            } else if (key === 'search') {
                chips.push({ key, type: 'search', label: `Search: "${value}"`, value });
            } else if (key === 'onSale') {
                if (value === 'true') {
                    chips.push({ key, type: 'onSale', label: 'On Sale Only', value });
                }
            } else {
                const items = value.split(',').filter(Boolean);
                items.forEach(item => {
                    const displayLabel = item.charAt(0).toUpperCase() + item.slice(1).replace(/-/g, ' ');
                    chips.push({ key, type: key, label: displayLabel, value: item });
                });
            }
        });
        
        return chips;
    };

    const removeFilterChip = (chip) => {
        if (['minPrice', 'maxPrice', 'search', 'onSale'].includes(chip.type)) {
            updateFilters({ [chip.type]: '' });
        } else {
            const currentVal = filters[chip.type] || '';
            const items = currentVal.split(',').filter(Boolean);
            const filteredItems = items.filter(i => i !== chip.value);
            updateFilters({ [chip.type]: filteredItems.join(',') });
        }
    };

    return (
        <main className="pt-[10px] pb-12">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Breadcrumb */}
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground">
                        <li><a href="/" className="hover:text-primary transition-colors">Home</a></li>
                        <li><i className="ri-arrow-right-s-line"></i></li>
                        {(filters.category || filters.brand) && (
                            <>
                                <li>
                                    <button
                                        onClick={() => updateFilters({ category: '', brand: '' })}
                                        className="hover:text-primary transition-colors"
                                    >
                                        Products
                                    </button>
                                </li>
                                <li><i className="ri-arrow-right-s-line"></i></li>
                            </>
                        )}
                        <li className="text-foreground font-medium">{getPageTitle()}</li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground mb-2">{getPageTitle()}</h1>
                        <p className="text-muted-foreground">
                            {loading ? 'Loading...' : `${pagination.total} product${pagination.total !== 1 ? 's' : ''} found`}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            onClick={() => setMobileFilterOpen(true)}
                            className="lg:hidden flex items-center gap-2 px-4 py-2 border-2 border-border rounded-lg hover:border-primary transition-colors whitespace-nowrap"
                        >
                            <i className="ri-filter-3-line"></i> Filters
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={filters.showAll === 'true'}
                                onChange={(e) => updateFilters({ showAll: e.target.checked ? 'true' : '' })}
                                className="w-4 h-4 accent-primary cursor-pointer"
                            />
                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                                Show manually out of stock
                            </span>
                        </label>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-muted-foreground whitespace-nowrap">Sort by:</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => handleSortChange(e.target.value)}
                                className="px-4 py-2 border-2 border-border rounded-lg text-sm cursor-pointer outline-none hover:border-primary transition-colors"
                            >
                                <option value="newest">Newest First</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                                <option value="name_asc">Name: A to Z</option>
                                <option value="name_desc">Name: Z to A</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex gap-6">
                    {/* Desktop Filter Panel - Sticky */}
                    <div className="hidden lg:block w-64 flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-hide pr-2">
                        <FilterPanel filters={filters} onFilterChange={updateFilters} baseFilters={{}} />
                    </div>

                    {/* Mobile Filter Drawer */}
                    {mobileFilterOpen && (
                        <div className="fixed inset-0 z-50 lg:hidden">
                            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFilterOpen(false)}></div>
                            <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-card overflow-y-auto">
                                <div className="sticky top-0 bg-card border-b border-border px-4 py-4 flex items-center justify-between">
                                    <h2 className="text-lg font-bold">Filters</h2>
                                    <button
                                        onClick={() => setMobileFilterOpen(false)}
                                        className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded-lg transition-colors"
                                    >
                                        <i className="ri-close-line text-xl"></i>
                                    </button>
                                </div>
                                <div className="p-4">
                                    <FilterPanel filters={filters} onFilterChange={(f) => { updateFilters(f); setMobileFilterOpen(false); }} baseFilters={{}} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Product Grid Area */}
                    <div className="flex-1">
                        {/* Active Filter Chips */}
                        {getActiveFilterChips().length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-4 bg-muted/40 p-2 rounded-lg border border-border/60">
                                <span className="text-xs text-muted-foreground font-medium mr-1 pl-1">Active:</span>
                                {getActiveFilterChips().map((chip, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-1.5 px-3 py-1 bg-card border border-border rounded-full text-xs font-medium hover:border-primary/50 transition-colors shadow-sm"
                                    >
                                        <span className="text-foreground">{chip.label}</span>
                                        <button
                                            onClick={() => removeFilterChip(chip)}
                                            className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                        >
                                            <i className="ri-close-line"></i>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => setSearchParams({}, { replace: true, preventScrollReset: true })}
                                    className="text-xs text-primary hover:text-primary/80 font-semibold ml-auto pr-1 transition-colors"
                                >
                                    Clear All
                                </button>
                            </div>
                        )}

                        {/* Loading Skeleton - only shown on initial load when no products exist */}
                        {loading && products.length === 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse flex flex-row md:flex-col">
                                        <div className="w-[38%] min-w-[120px] h-[132px] md:w-full md:h-64 bg-muted shrink-0"></div>
                                        <div className="p-3 md:p-4 space-y-3 flex-1">
                                            <div className="h-3 bg-muted rounded w-1/4"></div>
                                            <div className="h-4 bg-muted rounded w-3/4"></div>
                                            <div className="h-3 bg-muted rounded w-1/2"></div>
                                            <div className="h-6 bg-muted rounded w-1/3"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Error State */}
                        {error && !loading && (
                            <div className="text-center py-16">
                                <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                                <p className="text-muted-foreground mb-4">{error}</p>
                                <button
                                    onClick={fetchProducts}
                                    className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && !error && products.length === 0 && (
                            <div className="text-center py-16">
                                <i className="ri-inbox-line text-5xl text-muted-foreground/40 mb-4 block"></i>
                                <h3 className="text-xl font-semibold text-foreground mb-2">No items available</h3>
                                <p className="text-muted-foreground mb-6">
                                    {filters.brand || filters.category || filters.search
                                        ? 'No products match the selected filters. Try removing some filters or browse all products.'
                                        : 'Check back soon for new products!'}
                                </p>
                                {(filters.brand || filters.category || filters.search || filters.color || filters.minPrice || filters.maxPrice) && (
                                    <button
                                        onClick={() => setSearchParams({})}
                                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                                    >
                                        Browse All Products
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Products + Pagination */}
                        {products.length > 0 && (!loading || products.length > 0) && (
                            <div className={loading ? 'opacity-50 pointer-events-none transition-opacity duration-200' : 'transition-opacity duration-200'}>
                                <ProductGrid products={products} />

                                {pagination.totalPages > 1 && (
                                    <div className="flex justify-center items-center gap-2 mt-8">
                                        <button
                                            onClick={() => updateFilters({ page: currentPage - 1 })}
                                            disabled={currentPage <= 1}
                                            className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                                        >
                                            <i className="ri-arrow-left-s-line"></i>
                                        </button>

                                        {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                                            let pageNum;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => updateFilters({ page: pageNum })}
                                                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'bg-primary text-white'
                                                            : 'border border-border hover:border-primary'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => updateFilters({ page: currentPage + 1 })}
                                            disabled={currentPage >= pagination.totalPages}
                                            className="px-3 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors"
                                        >
                                            <i className="ri-arrow-right-s-line"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
